import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BookmarkSimple, FloppyDisk, X, Plus } from "@phosphor-icons/react";

// Saved Views dropdown. Lists project-level views (shared across collaborators).
// Clicking a view applies it; `Save current view…` captures the live state.
export default function SavedViewsMenu({ views, onApply, onSaveCurrent, onDelete, canEditProject }) {
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");

  const commit = async () => {
    const n = name.trim();
    if (!n) return;
    await onSaveCurrent(n);
    setSaving(false);
    setName("");
  };

  return (
    <DropdownMenu
      onOpenChange={(o) => {
        if (!o) {
          setSaving(false);
          setName("");
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 rounded-md border-slate-300 hover:border-slate-400 text-slate-700 font-medium shrink-0"
          data-testid="saved-views-btn"
          title="Saved views"
        >
          <BookmarkSimple size={14} weight="bold" />
          <span className="ml-2">Views</span>
          {views.length > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center h-[18px] min-w-[18px] px-1 rounded-md bg-slate-100 text-slate-600 text-[10px] font-mono-ibm">
              {views.length}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-md w-[280px]" data-testid="saved-views-menu">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest font-mono-ibm text-slate-500">
          Saved views
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {views.length === 0 && (
          <div className="px-3 py-4 text-xs text-slate-500 text-center">
            No saved views yet.
            <br />
            Set up the filters, zoom and columns you like, then save the current view below.
          </div>
        )}
        {views.map((v) => (
          <div
            key={v.id}
            className="flex items-center gap-1 px-1.5 py-0.5"
            data-testid={`saved-view-row-${v.id}`}
          >
            <button
              type="button"
              onClick={() => onApply(v)}
              className="flex-1 text-left px-2 py-1.5 rounded-md hover:bg-slate-50 flex flex-col min-w-0"
              data-testid={`saved-view-apply-${v.id}`}
            >
              <span className="text-xs font-medium text-slate-950 truncate">{v.name}</span>
              <span className="text-[10px] text-slate-400 font-mono-ibm truncate">
                {summarise(v)}
              </span>
            </button>
            {canEditProject && (
              <button
                type="button"
                onClick={() => onDelete(v.id)}
                className="h-7 w-7 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center shrink-0"
                title="Delete view"
                data-testid={`saved-view-delete-${v.id}`}
              >
                <X size={12} weight="bold" />
              </button>
            )}
          </div>
        ))}
        <DropdownMenuSeparator />
        {!saving ? (
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setSaving(true);
            }}
            className="text-xs"
            data-testid="saved-view-save-trigger"
          >
            <Plus size={12} weight="bold" className="mr-2" />
            Save current view…
          </DropdownMenuItem>
        ) : (
          <div className="p-2 space-y-2" data-testid="saved-view-save-form">
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="View name (e.g. Exec dashboard)"
              className="h-8 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-slate-950 text-xs"
              data-testid="saved-view-name-input"
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") {
                  setSaving(false);
                  setName("");
                }
              }}
            />
            <div className="flex gap-1.5">
              <Button
                size="sm"
                onClick={commit}
                disabled={!name.trim()}
                className="flex-1 h-7 rounded-md bg-slate-950 hover:bg-slate-800 text-white text-[11px]"
                data-testid="saved-view-save-confirm"
              >
                <FloppyDisk size={11} weight="bold" />
                <span className="ml-1.5">Save view</span>
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setSaving(false);
                  setName("");
                }}
                className="h-7 rounded-md border-slate-300 text-[11px] px-2"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function summarise(v) {
  const bits = [];
  if (v.zoom_key) bits.push(v.zoom_key.toUpperCase());
  if (v.workstream_filter) bits.push(`WS: ${v.workstream_filter}`);
  if (v.status_filter) bits.push(v.status_filter);
  if (v.critical_path_on) bits.push("CP on");
  if (v.hidden_cols && v.hidden_cols.length) bits.push(`${v.hidden_cols.length} hidden`);
  if (v.search) bits.push(`"${v.search}"`);
  return bits.join(" · ") || "Default view";
}
